import React, { useEffect, useState } from "react";
import { Plus, X, Check, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const COLS = [
  { key: "ToDo", label: "To do" },
  { key: "InProgress", label: "In progress" },
  { key: "Done", label: "Done" },
];

export default function Roadmap() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", client_id: "", client_approval_required: false, status: "ToDo" });

  const load = async () => {
    const [t, c] = await Promise.all([api.get("/roadmap"), api.get("/clients")]);
    setTasks(t.data);
    setClients(c.data);
  };

  useEffect(() => { load(); }, []);

  const move = async (id, status) => {
    await api.patch(`/roadmap/${id}`, { status });
    await load();
  };

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/roadmap", { ...form, client_id: form.client_id || null });
    toast.success("Task created");
    setForm({ title: "", description: "", client_id: "", client_approval_required: false, status: "ToDo" });
    setShowForm(false);
    await load();
  };

  const remove = async (id) => {
    await api.delete(`/roadmap/${id}`);
    await load();
  };

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <div className="flex items-start justify-between mb-10">
        <div>
          <p className="eyebrow mb-2">Pillar 4 · Roadmap</p>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="roadmap-title">Roadmap</h1>
        </div>
        <button onClick={() => setShowForm(true)} className="bg-slate-900 text-white rounded-full px-5 py-2.5 font-medium hover:bg-slate-700 active:scale-95 inline-flex items-center gap-2" data-testid="new-task-button">
          <Plus className="w-4 h-4" /> New task
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kanban-board">
        {COLS.map((col) => (
          <div key={col.key} className="bg-slate-50/80 rounded-2xl p-4 border border-slate-200 min-h-[500px]" data-testid={`col-${col.key}`}>
            <div className="flex items-center justify-between mb-4 px-2">
              <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wider">{col.label}</h3>
              <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded-full border border-slate-200">{tasks.filter((t) => t.status === col.key).length}</span>
            </div>
            <div className="space-y-3">
              {tasks.filter((t) => t.status === col.key).map((t) => {
                const cli = clients.find((c) => c.id === t.client_id);
                return (
                  <div key={t.id} className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm" data-testid={`task-${t.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="font-semibold text-slate-900 text-sm">{t.title}</h4>
                      <button onClick={() => remove(t.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    {t.description && <p className="text-xs text-slate-500 mb-3 line-clamp-2">{t.description}</p>}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        {cli ? cli.name : "Agency-wide"}
                        {t.submitted_by === "client" && <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">client</span>}
                        {t.client_approval_required && (
                          t.client_approved_at
                            ? <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"><Check className="w-3 h-3" />approved</span>
                            : <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">pending</span>
                        )}
                      </div>
                      <select value={t.status} onChange={(e) => move(t.id, e.target.value)} className="text-xs border border-slate-200 rounded-lg px-1 py-0.5 bg-slate-50" data-testid={`move-${t.id}`}>
                        {COLS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                    </div>
                  </div>
                );
              })}
              {tasks.filter((t) => t.status === col.key).length === 0 && (
                <p className="text-xs text-slate-400 text-center py-8">Nothing here yet.</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="new-task-modal">
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-2xl font-bold tracking-tight">New task</h2>
              <button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded-full"><X className="w-4 h-4" /></button>
            </div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="task-title-input" />
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="task-desc-input" />
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Client (optional)</label>
            <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="task-client-select">
              <option value="">Agency-wide</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="flex items-center gap-2 mb-6 text-sm cursor-pointer">
              <input type="checkbox" checked={form.client_approval_required} onChange={(e) => setForm({ ...form, client_approval_required: e.target.checked })} data-testid="task-approval-checkbox" />
              Requires client approval
            </label>
            <button type="submit" className="w-full bg-slate-900 text-white rounded-full py-3 font-medium hover:bg-slate-700 active:scale-95" data-testid="create-task-submit">Create task</button>
          </form>
        </div>
      )}
    </div>
  );
}
